import hideOrShowCTA from './cta.js'
import addLogoToPlayer from './brandLogo.js'
import { initializeIMA, loadAds } from './ads.js'

function isLocalStorageAvailable () {
  try {
    const testKey = '__storage_test__'
    window.localStorage.setItem(testKey, 'test')
    window.localStorage.removeItem(testKey)
    return true
  } catch (error) {
    return false
  }
}

const blockClick = (e) => {
  if (e.button === 0) {
    e.preventDefault()
    e.stopPropagation()
  }
}

const highlightChapterItem = (currentTime) => {
  // remove highlight from all chapter items
  // add highlight on selected chapter
  window.dispatchEvent(
    new CustomEvent('highlight-chapter', {
      detail: currentTime
    })
  )
}

export default class HTML5EventsAdapter {
  constructor (
    mediaElement,
    settings,
    ctaData,
    imageOverlay,
    t,
    muted = false,
    background = false,
    resumeWhereLeft = false,
    areControlsDisabled = false,
    captionLanguage,
    widgets = '[]',
    captionsOffOnLoad = false
  ) {
    this.mediaElement = mediaElement
    this.settings = settings
    this.ctaData = ctaData
    this.imageOverlay = imageOverlay
    this.mediaElEventHandlers = []
    this.t = t
    this.muted = muted
    this.background = background
    this.resumeWhereLeft = resumeWhereLeft
    this.captionLanguage = captionLanguage
    this.captionsOffOnLoad = captionsOffOnLoad
    let receiver
    if (window.playerjs) {
      receiver = new window.playerjs.Receiver()
    }
    this.receiver = receiver

    this.lastPlayedStartedAt = null
    this.videoQuartersPlaybackStatus = {
      25: false,
      50: false,
      75: false
    }
    this.lastProgressSaved = 0
    this.registerMediaElement()

    // add event listener to unmute the video when overlay unmute button is clicked
    const isUnmuteButton = document.querySelector('.overlay-unmute-button')
    if (isUnmuteButton) {
      isUnmuteButton.addEventListener('click', () => {
        this.mediaElement.muted = false
        this.mediaElement.volume = this.getVolumeFromLS() > 0 ? this.getVolumeFromLS() : 0.8
        document.querySelector('.overlay-unmute-button').style.display = 'none'
      })
    }

    const mediaSettingsMenu = document.querySelector('media-settings-menu')
    const mediaController = document.querySelector('media-controller')
    const mediaPlayButton = document.querySelector('media-play-button')
    if (areControlsDisabled && mediaController) {
      if(mediaController.hotkeys){
        mediaController.hotkeys.add('noarrowleft', 'noarrowright')
      }
    }
    this.mediaController = mediaController
    // check if media settings menu is hidden or not
    if (mediaSettingsMenu) {
      mediaSettingsMenu.addEventListener('media-settings-menu-hide', () => {
        if (this.mediaElement.muted) {
          document.querySelector('.overlay-unmute-button').style.display = 'block'
        }
      })
    }

    const observer = new window.MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
          if (mediaSettingsMenu.getAttribute(mutation.attributeName) != null) {
            mediaController.setAttribute('autohide', '1')
          } else {
            mediaController.setAttribute('autohide', '-1')
          }
        }
      })
    })

    observer.observe(mediaSettingsMenu, {
      attributes: true // Observe attribute changes
    })

    const isLiveButton = document.querySelector('.live-button')
    if (isLiveButton) {
      isLiveButton.addEventListener('click', () => {
        if (window.hlsPlayer) {
          const duration = window.hlsPlayer.liveSyncPosition
          this.mediaElement.currentTime = duration
        }
      })
    }

    // handle chapters menu item click
    if (window) {
      window.moveToChapter = (startTime) => {
        this.mediaElement.currentTime = startTime
        highlightChapterItem(startTime)
      }
    }

    // focus on player on page load to enable keyboard controls
    const controller = document.querySelector('hls-video') || document.querySelector('shaka-video') || document.querySelector('html5-video')
    if (mediaPlayButton) {
      mediaPlayButton.addEventListener('click', () => {
        window.requestAnimationFrame(() => controller?.focus())
      })
    }

    // add support for extra keyboard controls which are not supported by media chrome
    if (!areControlsDisabled) {
      document.addEventListener('keyup', (event) => {
        if (event.key === 'j') {
          this.mediaElement.currentTime -= 10
        }

        if (event.key === 'l') {
          this.mediaElement.currentTime += 10
        }
      })
    }

    document.addEventListener('keyup', (event) => {
      // if up and down arrow are pressed to increase and decrease volume
      if (event.key === 'ArrowUp') {
        try {
          this.mediaElement.volume += 0.1
        } catch (error) {
          this.mediaElement.volume = 1
        }
      }

      if (event.key === 'ArrowDown') {
        try {
          this.mediaElement.volume -= 0.1
        } catch (error) {
          this.mediaElement.volume = 0
        }
      }

      if (event.shiftKey && event.key === '>') {
        // increase playback rate by 0.25 and a maximum upto 2
        this.mediaElement.playbackRate += 0.25
        if (this.mediaElement.playbackRate > 2) {
          this.mediaElement.playbackRate = 2
        }
      }

      if (event.shiftKey && event.key === '<') {
        // decrease playback rate by 0.25 and a minimum upto 0.25
        this.mediaElement.playbackRate -= 0.25
        if (this.mediaElement.playbackRate < 0.25) {
          this.mediaElement.playbackRate = 0.25
        }
      }
    })
    this.widgets = JSON.parse(widgets)
    for (const widget of this.widgets) {
      if (widget.visibility && widget.visibility === 'before-playback') {
        this.showWidget(widget)
      }
    }
  }

  showWidget (widget) {
    // only made thinking form for now
    // make changes to consider other types of widget
    if (isLocalStorageAvailable()) {
      // TODO: this key should be made more secure so that users dont add them themselves
      const widgetSubmitted = window.localStorage.getItem(widget.cookieId)
      if (!widgetSubmitted) {
        this.mediaElement.pause()
        const formEmbedPlayerInnerWrapper = document.querySelector('.form-embed-player-innerwrapper')
        if (formEmbedPlayerInnerWrapper) {
          formEmbedPlayerInnerWrapper.style.display = 'flex'
        }
      }
    }
    // TODO: Ask Anshul: what to do if cookie is disabled in the browser
  }

  hideOrShowWidget (currentTime) {
    if (!this.widgets || !Array.isArray(this.widgets)) {
      return
    }
    for (const widget of this.widgets) {
      // TODO: make changes to consider other types of widget in future
      if (widget.visibility && widget.visibility === 'during-playback' && currentTime >= widget.visibilityWindow.startTime && !window.widgetSkippedOrSubmitted) {
        this.showWidget(widget)
      }
    }
  }

  setTimeUpdateLS (duration) {
    if (isLocalStorageAvailable()) {
      window.localStorage.setItem('progress_time_' + this.settings.videoId, duration)
      this.lastProgressSaved = duration
    }
  }

  getLastPlayerTimeFromLS () {
    if (isLocalStorageAvailable()) {
      return window.localStorage.getItem('progress_time_' + this.settings.videoId) || 0
    }
    return 0
  }

  getPlaybackRateFromLS () {
    if (isLocalStorageAvailable()) {
      return window.localStorage.getItem('playback_rate') || 1
    }
    return 1
  }

  getVolumeFromLS () {
    if (isLocalStorageAvailable()) {
      return window.localStorage.getItem('volume') || 1
    }
    return 1
  }

  registerMediaElement () {
    if (!this.mediaElement) {
      return
    }

    this.listenToMediaElementEvent('loadedmetadata', () => {
      if (window && window.google && this.settings && this.settings.vastTagURL) {
        initializeIMA(this.mediaElement, this.settings.vastTagURL)
      }
      if (!this.muted) {
        this.mediaElement.volume = this.getVolumeFromLS()
      } else {
        this.mediaElement.muted = true
        this.mediaElement.volume = 0
      }

      if (window?.ReactNativeWebView && window?.ReactNativeWebView?.postMessage) {
        // if (
        //   window.webkit &&
        //   window.webkit.messageHandlers &&
        //   window.webkit.messageHandlers.ReactNativeHistoryShim &&
        //   typeof window.webkit.messageHandlers.ReactNativeHistoryShim.postMessage === 'function'
        // ) {
        //   window.webkit.messageHandlers.ReactNativeHistoryShim.postMessage(JSON.stringify({ key: 'onLoad' }))
        // } else {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          key: 'onLoad'
        }))
        // }
      }

      if (this.receiver) {
        this.receiver.ready()
        this.setUpReceiverEvents()
      }
      // hide the overlay with loader when video is loaded and ready to play
      document.querySelector('.media-overlay').style.display = 'none'
      addLogoToPlayer(this.imageOverlay)

      // override subtitles on load if captionLanguage is provided
      if (this.captionLanguage) {
        for (const track of this.mediaElement.textTracks) {
          if (track.language === this.captionLanguage) {
            track.mode = 'showing'
          } else {
            track.mode = 'disabled'
          }
        }
      }
      
      if(this.captionsOffOnLoad){
        this.mediaController.dispatchEvent(
          new CustomEvent('mediatogglesubtitlesrequest', {
            detail: false, // explicitly request "disabled"
            bubbles: true,
            composed: true,
          }),
        );
      }
    })

    this.listenToMediaElementEvent('canplay', () => {
      this.mediaElement.currentTime = this.t ? this.t : this.resumeWhereLeft ? this.getLastPlayerTimeFromLS() : 0
      this.mediaElement.playbackRate = this.getPlaybackRateFromLS()

      // start from t=? time
      // if (
      //     GUMLET_EMBED.gumlet_embed[this.itemIndex].start_at &&
      //     GUMLET_EMBED.gumlet_embed[this.itemIndex].start_at > 0
      // ) {
      //     this.mediaElement.currentTime = GUMLET_EMBED.gumlet_embed[this.itemIndex].start_at;
      // }

      // if (
      //     GUMLET_EMBED.gumlet_embed[this.itemIndex].resumable &&
      //     isLocalStorageAvailable()
      // ) {
      //     let lastTime = localStorage.getItem("currentTime_" + GUMLET_EMBED.gumlet_embed[this.itemIndex].video_id);
      //     if (lastTime !== null && lastTime > player.currentTime) {
      //         this.mediaElement.currentTime = parseFloat(lastTime);
      //     }
      // }
    }, true)

    this.listenToMediaElementEvent('play', () => {
      if (typeof navigator.vibrate === 'function') navigator.vibrate(10)
      if (window && window.google && this.settings && this.settings.vastTagURL) {
        loadAds()
      }
      // setting current asset_id only if autoplay is false
      if (isLocalStorageAvailable() && !this.mediaElement?.autoplay) { // Autoplay -> false
        window.localStorage.setItem('currently_playing', this.settings.videoId)
      }
      this.lastPlayedStartedAt = this.mediaElement.currentTime

      if (this.settings && this.settings.facebookPixelId && window.fbq) {
        window.fbq('trackCustom', 'Played', {
          title: this.settings.videoTitle,
          asset_id: this.settings.videoId
        })
      }

      if (this.settings && this.settings.gaTrackingId && window.gtag) {
        window.gtag('event', 'Played', {
          title: this.settings.videoTitle,
          asset_id: this.settings.videoId
        })
      }

      hideOrShowCTA({
        currentTime: this.mediaElement.currentTime,
        callToActions: this.ctaData,
        settings: this.settings
      })
      highlightChapterItem(this.mediaElement.currentTime)
      this.hideOrShowWidget(this.mediaElement.currentTime)

      if (this.receiver) {
        this.receiver.emit('play')
      }
      if (document.querySelector('.video-title-div')) {
        document.querySelector('.video-title-div').style.display = 'none'
      }
    })

    this.listenToMediaElementEvent('pause', () => {
      if (typeof navigator.vibrate === 'function') navigator.vibrate(10)
      const playingDuration = this.mediaElement.currentTime - this.lastPlayedStartedAt
      if (this.settings && this.settings.facebookPixelId && window.fbq) {
        window.fbq('trackCustom', 'Seconds Played', {
          title: this.settings.videoTitle,
          seconds: playingDuration,
          asset_id: this.settings.videoId
        })
      }

      if (this.settings && this.settings.gaTrackingId && window.gtag) {
        window.gtag('event', 'Seconds Played', {
          title: this.settings.videoTitle,
          seconds: playingDuration,
          asset_id: this.settings.videoId
        })
      }

      hideOrShowCTA({
        currentTime: this.mediaElement.currentTime,
        callToActions: this.ctaData,
        settings: this.settings
      })
      highlightChapterItem(this.mediaElement.currentTime)
      this.hideOrShowWidget(this.mediaElement.currentTime)

      if (this.receiver) {
        this.receiver.emit('pause')
      }
    })

    this.listenToMediaElementEvent('ratechange', () => {
      if (this.receiver) {
        this.receiver.emit('playbackRateChange', {
          speed: this.mediaElement.playbackRate
        })
      };
      // set localstorage for playback rate
      if (this.mediaElement.playbackRate !== 0 && isLocalStorageAvailable()) {
        window.localStorage.setItem('playback_rate', this.mediaElement.playbackRate)
      }
    })

    this.listenToMediaElementEvent('playing', () => {
      const htmlElement = document.getElementsByTagName('body')[0]
      if (this.background && htmlElement) {
        // Check if the listener is already added to prevent duplicates
        if (!htmlElement.hasAttribute('data-click-disabled')) {
          htmlElement.addEventListener('click', blockClick, true)
          htmlElement.addEventListener('dblclick', blockClick, true)
          htmlElement.setAttribute('data-click-disabled', 'true')
        }
      }
    })

    this.listenToMediaElementEvent('error', () => {
      if (this.receiver) {
        this.receiver.emit('error')
      }
    })

    this.listenToMediaElementEvent('volumechange', (event) => {
      if (this.receiver) {
        this.receiver.emit('volumeChange', {
          volume: this.mediaElement.volume
        })
      }

      // we have a custom overlay with an unmute which is visible when a video is autoplayed and hence muted,
      // so we need to hide it when volume is changed fires in case of unmute as well
      if (document.querySelector('.overlay-unmute-button') && this.mediaElement.volume > 0) {
        document.querySelector('.overlay-unmute-button').style.display = 'none'
      } else if (document.querySelector('.overlay-unmute-button') && this.mediaElement.volume === 0) {
        document.querySelector('.overlay-unmute-button').style.display = 'block'
      }

      // set localstorage for volume
      // https://github.com/muxinc/media-chrome/issues/803
      // due to this bug we are having this hack.
      if (isLocalStorageAvailable() && this.mediaElement.volume !== 0 && this.mediaElement.volume !== 0.25) {
        window.localStorage.setItem('volume', this.mediaElement.volume)
      } else if (isLocalStorageAvailable() && this.mediaElement.volume !== 0 && !this.mediaElement.muted) {
        this.mediaElement.volume = window.localStorage.getItem('volume') || 0.25
      }
    })

    this.listenToMediaElementEvent('progress', () => {
      // get percentage of video buffered
      try {
        const end = this.mediaElement.buffered.end(0)
        const soFar = end / this.mediaElement.duration
        const percent = soFar * 100
        if (this.receiver) {
          this.receiver.emit('progress', {
            percent
          })
        }
      } catch (e) {
        // sometimes it gives an error
        // HTML5EventsAdapter.js:139 Uncaught DOMException: Failed to execute 'end' on 'TimeRanges':
        // The index provided (0) is greater than or equal to the maximum bound (0).
        // at HTML5EventsAdapter.eval (
      }
    })

    this.listenToMediaElementEvent('seeking', () => {
    })

    this.listenToMediaElementEvent('seeked', () => {
      hideOrShowCTA({
        currentTime: this.mediaElement.currentTime,
        callToActions: this.ctaData,
        settings: this.settings
      })
      highlightChapterItem(this.mediaElement.currentTime)
      this.hideOrShowWidget(this.mediaElement.currentTime)

      // update local storage time progress
      this.setTimeUpdateLS(this.mediaElement.currentTime)
      if (this.receiver) {
        this.receiver.emit('seeked', {
          seconds: this.mediaElement.currentTime,
          duration: this.mediaElement.duration
        })
      }
    })

    this.listenToMediaElementEvent('ended', () => {
      hideOrShowCTA({
        currentTime: this.mediaElement.currentTime,
        callToActions: this.ctaData,
        settings: this.settings
      })
      highlightChapterItem(this.mediaElement.currentTime)
      this.hideOrShowWidget(this.mediaElement.currentTime)

      // update local storage time progress to 0
      this.setTimeUpdateLS(0)
      if (this.settings && this.settings.facebookPixelId && window.fbq) {
        window.fbq('trackCustom', '100 Percent Played', {
          title: this.settings.videoTitle,
          asset_id: this.settings.videoId
        })
      }
      if (this.settings && this.settings.gaTrackingId && window.gtag) {
        window.gtag('event', '100 Percent Played', {
          title: this.settings.videoTitle,
          asset_id: this.settings.videoId
        })
      }

      if (this.receiver) {
        this.receiver.emit('ended')
      }
      for (const widget of this.widgets) {
        if (widget.visibility && widget.visibility === 'after-playback') {
          this.showWidget(widget)
        }
      }

      if (window?.ReactNativeWebView && window?.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          key: 'videoEnded'
        }))
      }
    })

    this.listenToMediaElementEvent('timeupdate', () => {
      hideOrShowCTA({
        currentTime: this.mediaElement.currentTime,
        callToActions: this.ctaData,
        settings: this.settings
      })
      highlightChapterItem(this.mediaElement.currentTime)
      this.hideOrShowWidget(this.mediaElement.currentTime)

      // save current time progress in local storage
      if (this.mediaElement.currentTime > this.lastProgressSaved + 5) {
        this.setTimeUpdateLS(this.mediaElement.currentTime)
      }

      if (
        this.settings &&
                (
                  this.settings.facebookPixelId ||
                    this.settings.gaTrackingId
                )
      ) {
        const percentPlayed = Math.round((this.mediaElement.currentTime / this.mediaElement.duration) * 100)
        let eventName = null

        if (percentPlayed >= 25 && !this.videoQuartersPlaybackStatus[25]) {
          this.videoQuartersPlaybackStatus[25] = true
          eventName = '25 Percent Played'
        } else if (percentPlayed >= 50 && !this.videoQuartersPlaybackStatus[50]) {
          this.videoQuartersPlaybackStatus[50] = true
          eventName = '50 Percent Played'
        } else if (percentPlayed >= 75 && !this.videoQuartersPlaybackStatus[75]) {
          this.videoQuartersPlaybackStatus[75] = true
          eventName = '75 Percent Played'
        }

        if (eventName && this.settings && this.settings.gaTrackingId && window.gtag) {
          window.gtag('event', eventName, {
            title: this.settings.videoTitle,
            asset_id: this.settings.videoId
          })
        }
        if (eventName && this.settings && this.settings.facebookPixelId && window.fbq) {
          window.fbq('trackCustom', eventName, {
            title: this.settings.videoTitle,
            asset_id: this.settings.videoId
          })
        }
      }

      /** Logic
             * If the video is not autoplay and the video is not the same video that is currently playing, then pause the video
             */
      if (isLocalStorageAvailable() && !this.mediaElement?.autoplay && window.localStorage.getItem('currently_playing') && (window.localStorage.getItem('currently_playing') !== this.settings.videoId)) {
        this.mediaElement.pause()
      }

      if (this.receiver) {
        this.receiver.emit('timeupdate', {
          seconds: this.mediaElement.currentTime,
          duration: this.mediaElement.duration
        })
      }

      if (window?.ReactNativeWebView && window?.ReactNativeWebView?.postMessage) {
        // if (
        //   window.webkit &&
        //   window.webkit.messageHandlers &&
        //   window.webkit.messageHandlers.ReactNativeHistoryShim &&
        //   typeof window.webkit.messageHandlers.ReactNativeHistoryShim.postMessage === 'function'
        // ) {
        //   window.webkit.messageHandlers.ReactNativeHistoryShim.postMessage(JSON.stringify({
        //     key: 'timeupdate',
        //     data: { seconds: this.mediaElement.currentTime, duration: this.mediaElement.duration }
        //   }))
        // } else {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          key: 'timeupdate',
          data: { seconds: this.mediaElement.currentTime, duration: this.mediaElement.duration }
        }))
        // }
      }
    })

    this.listenToMediaElementEvent('stalled', () => {
    })

    this.listenToMediaElementEvent('waiting', () => {
    })

    const handleFullscreenChange = () => {
      if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) {
        if (this.receiver) {
          this.receiver.emit('fullscreenChange', true)
        }
        let videoOrientation = 'landscape'
        if (
          this.settings.aspectRatio.split(':').length === 2 &&
                    parseInt(this.settings.aspectRatio.split(':')[0]) < parseInt(this.settings.aspectRatio.split(':')[1])
        ) {
          videoOrientation = 'portrait'
        }
        if (videoOrientation === 'landscape') {
          try {
            window.screen.orientation.lock('landscape').catch(() => {
            })
          } catch (error) { }
          // only iphone requires the webkitenterfullscreen thing.
          if (/iPhone/.test(navigator.userAgent)) {
            try {
              this.mediaElement.webkitEnterFullscreen()
            } catch (e) { }
          }
        }
      } else {
        try {
          window.screen.orientation.lock('portrait').catch(() => {
          })
        } catch (error) { }

        if (this.receiver) {
          this.receiver.emit('fullscreenChange', false)
        }
      }
    }

    // Handle full screen enter and exit events
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange) // For WebKit (Safari)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange) // For Firefox
    document.addEventListener('MSFullscreenChange', handleFullscreenChange) // For IE/Edge
  }

  listenToMediaElementEvent (event, handler, isOnce = false) {
    if (!this.mediaElement) {
      throw new Error('No media attached')
    }
    const boundHandler = handler.bind(this)

    this.mediaElEventHandlers.push({ handler: boundHandler, event })
    this.mediaElement.addEventListener(event, boundHandler, isOnce ? { once: true } : false)
  }

  unregisterMediaElement () {
    if (!this.mediaElement) {
      throw new Error('No media attached')
    }

    this.mediaElEventHandlers.forEach((eventListener) => {
      this.mediaElement.removeEventListener(eventListener.event, eventListener.handler, false)
    })

    this.resetMedia()
  }

  setUpReceiverEvents () {
    // Can be added as debug logs
    // if (!this.receiver) {
    //     throw new Error('No receiver attached');
    // }

    if (this.receiver) {
      /* Playerjs receiver METHODS */
      this.receiver.on('play', () => {
        this.mediaElement.play()
      })

      this.receiver.on('pause', () => {
        this.mediaElement.pause()
      })

      this.receiver.on('getPaused', (callback) => {
        callback(this.mediaElement.paused)
      })

      this.receiver.on('getCurrentTime', (callback) => {
        callback(this.mediaElement.currentTime)
      })

      this.receiver.on('setCurrentTime', (value) => {
        this.mediaElement.currentTime = value
      })

      this.receiver.on('getDuration', (callback) => {
        callback(this.mediaElement.duration)
      })

      this.receiver.on('getVolume', (callback) => {
        const vol = this.mediaElement.volume * 100
        callback(vol)
      })

      this.receiver.on('setVolume', (value) => {
        this.mediaElement.volume = value / 100
      })

      this.receiver.on('mute', () => {
        this.mediaElement.muted = true
      })

      this.receiver.on('unmute', () => {
        this.mediaElement.muted = false
      })

      this.receiver.on('getMuted', (callback) => {
        callback(this.mediaElement.muted)
      })

      this.receiver.on('getLoop', (callback) => {
        callback(this.mediaElement.loop)
      })

      this.receiver.on('setLoop', (value) => {
        this.mediaElement.loop = value
      })

      this.receiver.on('disableSeek', (value) => {
        document.querySelector('media-time-range').style.pointerEvents = 'none'
        document.querySelector('media-seek-forward-button').style.pointerEvents = 'none'
        document.querySelector('media-seek-backward-button').style.pointerEvents = 'none'
      })

      // handle this in shaka player and HLS player
      // this.receiver.on('setPlaybackRate', function (value) {
      //     if (player.options.speed.includes(value)) {
      //         player.speed = value;
      //     }
      // });

      this.receiver.on('getPlaybackRate', (callback) => {
        callback(this.mediaElement.speed)
      })
    }
  }
}
